/**
 * Asking the box which wards exist, for the two screens that have to offer a choice.
 *
 * The ward list's picker and the registration form's ward field both need the directory, and what
 * must not drift between them is the **fetch policy** rather than the markup: one request, aborted
 * on unmount, a `WardListError` swallowed because the screen underneath is already reporting the
 * same unreachable box, and **never a refetch**.
 *
 * That last rule is why this is shared rather than copied. Every read of the directory writes an
 * audit row that no `DELETE` can take back out (AGENTS.md §3, §4), so a dependency list that
 * re-ran the effect would bill a clinician's use of a dropdown to the audit log — and the mistake
 * would be invisible in the interface. Copying thirty lines of effect into a second component is
 * exactly how one copy ends up with the dependency list the other does not have.
 *
 * The two screens still render their own control. On the ward list the picker is a choice about
 * what to look at; on the registration form it is a statement about where a patient is being
 * admitted, with a label matching the one the overview reads the value back under. Same data,
 * different sentence, so only the asking is shared.
 */

import { useEffect, useRef, useState } from 'react'

import { fetchWardDirectory, WardListError, type WardSummary } from './wardApi'

/**
 * The wards, or null while the answer is outstanding **or** could not be had.
 *
 * One value for both, because both callers render the same thing for either: no choice. The ward
 * list and the registration form each say what they can about the ward without one, and two
 * notices for one unreachable box read as two faults.
 *
 * @param selected The ward code currently chosen, or null if none is.
 * @param onSelect Called once with the first ward if nothing is selected by the time the directory
 *   arrives, so a screen opens on a list rather than on an empty frame with a dropdown
 *   (PHASE-0.md §2.2). Never called again — a clinician's later choice is theirs.
 * @returns The wards in the order the box returned them, or null.
 */
export function useWardDirectory(
  selected: string | null,
  onSelect: (ward: string) => void,
): readonly WardSummary[] | null {
  const [wards, setWards] = useState<readonly WardSummary[] | null>(null)

  /**
   * The current selection and callback, for the one read that happens after an await.
   *
   * A ref rather than a dependency, and this is the whole reason the effect below can have an
   * empty dependency list: re-running it on a selection change would refetch the directory every
   * time a clinician picked a ward, and every fetch is an audit row (see the module comment). The
   * values are read only when the directory arrives, which is after any render that would have
   * updated them, so what is read is current rather than stale.
   */
  const latest = useRef({ selected, onSelect })

  useEffect(() => {
    latest.current = { selected, onSelect }
  })

  useEffect(() => {
    const controller = new AbortController()

    fetchWardDirectory(controller.signal).then(
      (directory) => {
        if (controller.signal.aborted) {
          return
        }

        setWards(directory.wards)

        // Destructured rather than indexed, because an empty directory is a real answer and
        // `wards[0]` on one is `undefined`. `noUncheckedIndexedAccess` is what says so.
        const [first] = directory.wards

        // Only when nothing was selected — otherwise coming back from a patient would silently
        // move the clinician to a different ward than the one they left.
        if (latest.current.selected === null && first !== undefined) {
          latest.current.onSelect(first.ward)
        }
      },
      (cause: unknown) => {
        if (controller.signal.aborted) {
          return
        }

        // A failure leaves the state null, which is what both callers render for a pending fetch
        // too. Anything that is not a `WardListError` is a bug in `wardApi` rather than an
        // unreachable box, and is left to surface.
        if (!(cause instanceof WardListError)) {
          throw cause
        }
      },
    )

    return () => controller.abort()
  }, [])

  return wards
}
